/**
 * Guyana national health-voucher submission layout.
 *
 * Mirrors the government submission spreadsheet exactly: column A (blank on data
 * rows) through CI, in order — the same 87 columns the Ministry template uses.
 * `VOUCHER_COLUMNS` drives both the on-screen grid and the tab-separated clipboard
 * copy, so a pasted block lands cell-for-cell in the master workbook.
 *
 * Each column declares how it is filled:
 *   - blank:  always empty (manual columns the LIS doesn't capture, e.g. middle
 *             name, Region-4 breakdown, past history, blood film, Hb electrophoresis)
 *   - field:  fn(rec, ctx) -> string   (demographics, invoice, dates)
 *   - test:   { code, unit }           (a catalog testCode's value, or its unit)
 *   - fn:     fn(resultMap) -> string  (assembled cells, e.g. urinalysis)
 *
 * Kept as data (not code baked into the page) so the layout can be eyeballed
 * against the Ministry sheet and adjusted in one place.
 */

// ---- small formatters -------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

// Capitalize the first letter, lowercase the rest — e.g. "female " -> "Female".
export const capFirst = (s) => {
    const t = String(s || '').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
};

/**
 * Format a date as dd/mm/yyyy.
 *  - A plain 'YYYY-MM-DD' (a DOB) is reformatted by its parts, so it is NEVER
 *    shifted by a timezone (new Date('1963-10-15') is UTC midnight and would roll
 *    back a day in Guyana). '' when the value can't be understood.
 *  - A full timestamp (an order/collection instant) is formatted in the clinic
 *    timezone `tz` when given, so the printed calendar date is the local visit
 *    date, not the API server's or the browser's.
 */
export function formatDMY(value, tz) {
    if (!value) return '';
    const s = String(value);
    const plain = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (plain) return `${plain[3]}/${plain[2]}/${plain[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s; // free text the clerk typed — pass through
    try {
        const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
        return new Intl.DateTimeFormat('en-GB', tz ? { ...opts, timeZone: tz } : opts).format(d);
    } catch (e) {
        return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
    }
}

// Whole-year age from a DOB; '' when the DOB isn't a real date. Parses a plain
// 'YYYY-MM-DD' by its parts (no timezone drift across the birthday boundary).
export function computeAge(dob) {
    if (!dob) return '';
    const s = String(dob);
    const plain = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    let y, m, d;
    if (plain) { y = +plain[1]; m = +plain[2]; d = +plain[3]; }
    else {
        const dt = new Date(s);
        if (Number.isNaN(dt.getTime())) return '';
        y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
    }
    const now = new Date();
    let age = now.getFullYear() - y;
    const mm = (now.getMonth() + 1) - m;
    if (mm < 0 || (mm === 0 && now.getDate() < d)) age -= 1;
    return age >= 0 && age < 200 ? String(age) : '';
}

// "Date Voucher Used" = the visit date. Prefer specimen collection, then order —
// both are instants, so format them in the clinic timezone (ctx.tz).
export const voucherDate = (rec, ctx) => formatDMY(rec.collectedAt || rec.orderedAt, ctx.tz);

// Build a testCode -> { value, unit } lookup for one record.
export function resultMap(rec) {
    const m = new Map();
    (rec.results || []).forEach((r) => { if (r.testCode) m.set(r.testCode, r); });
    return m;
}

// Assemble a "Label: value" summary for a group of testCodes present in results.
const assemble = (map, pairs) =>
    pairs
        .map(([code, label]) => {
            const r = map.get(code);
            const v = r && r.value != null ? String(r.value).trim() : '';
            return v ? `${label}: ${v}` : '';
        })
        .filter(Boolean)
        .join('; ');

// Urinalysis groupings (component testCodes -> the three template cells).
const URINE_PHYSICAL = [['UACOLOUR', 'Colour'], ['UACLARITY', 'Clarity'], ['UASG', 'SG']];
const URINE_CHEMICAL = [
    ['UAGLU', 'Glucose'], ['UABILI', 'Bilirubin'], ['UAKET', 'Ketones'], ['UABLOOD', 'Blood'],
    ['UAPH', 'pH'], ['UAPROT', 'Protein'], ['UAURO', 'Urobilinogen'], ['UANIT', 'Nitrite'], ['UALEU', 'Leukocytes'],
];
const URINE_MICRO = [
    ['UAWBC', 'WBC'], ['UARBC', 'RBC'], ['UAEPI', 'Epithelial'], ['UABACT', 'Bacteria'], ['URINEMICRO', 'Micro'],
];

// ---- convenience column builders -------------------------------------------

export const blank = (header, group) => ({ header, group, blank: true });
export const field = (header, get, group, extra) => ({ header, group, field: get, ...extra });
// A test value column (num: paste as a number) + its paired UNIT column (text),
// sharing a section group.
const val = (header, code, group) => ({ header, group, test: { code, unit: false }, num: true });
const unit = (header, code, group) => ({ header, group, test: { code, unit: true } });

// ---- the 87 columns, A..CI, in order ---------------------------------------

export const VOUCHER_COLUMNS = [
    // A — blank on data rows (letterhead lives in the sheet's own header rows)
    blank(''),

    // B..R — voucher + demographics
    field('Invoice No.', (rec) => rec.invoice?.invoiceNumber || ''),
    field('Service Provider Name', (rec, ctx) => ctx.providerName || ''),
    field('Date Voucher Used (dd/mm/yyyy)', voucherDate),
    field('Voucher Serial No.', (rec) => rec.invoice?.voucherSerialNo || ''),
    field('User First Name(s)', (rec) => rec.patient?.fName || ''),
    blank('User Middle Name(s)'),
    field('User Last Name(s)', (rec) => rec.patient?.lName || ''),
    field('User ID or Passport No.', (rec) => rec.patient?.nationalId || ''),
    field('User Sex', (rec) => capFirst(rec.patient?.gender)),
    field('User Age', (rec) => computeAge(rec.patient?.dob), undefined, { num: true }),
    field('User DOB (dd/mm/yyyy)', (rec) => formatDMY(rec.patient?.dob)),
    field('User Address', (rec) => rec.patient?.address || ''),
    field('User Region', (rec) => rec.patient?.region || ''),
    blank('Region 4 Breakdown'),
    field('User Telephone No.', (rec) => rec.patient?.contactNumber || ''),
    field('User Ethnicity', (rec) => rec.patient?.ethnicity || ''),
    blank('User Past Medical History (Disease)'),

    // S..AH — CBC
    val('RBC Count', 'RBC', 'CBC'),
    unit('RBC Count UNIT', 'RBC', 'CBC'),
    val('WBC Count', 'WBC', 'CBC'),
    unit('WBC Count UNIT', 'WBC', 'CBC'),
    val('Lymphocytes', 'LYMPH', 'CBC'),
    val('Neutrophils', 'NEUT', 'CBC'),
    val('Monocytes', 'MONO', 'CBC'),
    val('Eosinophils', 'EOS', 'CBC'),
    val('Basophils', 'BASO', 'CBC'),
    unit('Differential UNIT (mm^3 OR %)', 'LYMPH', 'CBC'),
    val('Platelets', 'PLT', 'CBC'),
    unit('Platelets UNIT', 'PLT', 'CBC'),
    val('Hb', 'HGB', 'CBC'),
    unit('Hb UNIT', 'HGB', 'CBC'),
    val('Hematocrit', 'HCT', 'CBC'),
    unit('Hematocrit UNIT', 'HCT', 'CBC'),

    // AI..AP — RBC indices
    val('MCV', 'MCV', 'RBC Indices'),
    unit('MCV (fL)', 'MCV', 'RBC Indices'),
    val('MCH', 'MCH', 'RBC Indices'),
    unit('MCH (pg or fmol)', 'MCH', 'RBC Indices'),
    val('MCHC', 'MCHC', 'RBC Indices'),
    unit('MCHC (g/dL or g/L)', 'MCHC', 'RBC Indices'),
    val('RDW', 'RDW', 'RBC Indices'),
    unit('RDW (% or fL)', 'RDW', 'RBC Indices'),

    // AQ — Blood Film (not captured in catalog)
    blank('Blood Film Abnormal Findings', 'Blood Film'),

    // AR — FOBT
    val('Faecal Occult Blood Test (FOBT)', 'OCCULT', 'FOBT'),

    // AS..AT — HbA1c
    val('Hemoglobin A1C (HbA1c)', 'HBA1C', 'HbA1c'),
    unit('HbA1c (% or mmol/mol)', 'HBA1C', 'HbA1c'),

    // AU..AZ — Hemoglobin electrophoresis (not captured in catalog)
    blank('Hb A', 'Hemoglobin Electrophoresis'),
    blank('Hb A2', 'Hemoglobin Electrophoresis'),
    blank('Hb F', 'Hemoglobin Electrophoresis'),
    blank('Hb S', 'Hemoglobin Electrophoresis'),
    blank('Hb C', 'Hemoglobin Electrophoresis'),
    blank('Hb E', 'Hemoglobin Electrophoresis'),

    // BA..BD — Kidney function
    val('BUN', 'BUN', 'Kidney Function Test'),
    unit('BUN UNIT', 'BUN', 'Kidney Function Test'),
    val('Creatinine', 'CREAT', 'Kidney Function Test'),
    unit('Creatinine UNIT', 'CREAT', 'Kidney Function Test'),

    // BE..BN — Lipid profile
    val('Cholesterol', 'CHOL', 'Lipid Profile'),
    unit('Cholesterol UNIT', 'CHOL', 'Lipid Profile'),
    val('Triglycerides', 'TRIG', 'Lipid Profile'),
    unit('Triglycerides UNIT', 'TRIG', 'Lipid Profile'),
    val('HDL', 'HDL', 'Lipid Profile'),
    unit('HDL UNIT', 'HDL', 'Lipid Profile'),
    val('LDL', 'LDL', 'Lipid Profile'),
    unit('LDL UNIT', 'LDL', 'Lipid Profile'),
    val('Coronary Heart Disease Risk', 'CRR', 'Lipid Profile'),
    unit('Coronary Heart Disease Risk (%)', 'CRR', 'Lipid Profile'),

    // BO..BX — Liver function
    val('Aspartate transaminase (AST/SGOT)', 'AST', 'Liver Function Test'),
    unit('AST/SGOT UNIT', 'AST', 'Liver Function Test'),
    val('Alanine aminotransferase (ALT/SGPT)', 'ALT', 'Liver Function Test'),
    unit('ALT/SGPT UNIT', 'ALT', 'Liver Function Test'),
    val('Total Bilirubin', 'TBIL', 'Liver Function Test'),
    unit('Total Bilirubin UNIT', 'TBIL', 'Liver Function Test'),
    val('Direct Bilirubin', 'DBIL', 'Liver Function Test'),
    unit('Direct Bilirubin UNIT', 'DBIL', 'Liver Function Test'),
    val('Alkaline Phosphatase', 'ALP', 'Liver Function Test'),
    unit('Alkaline Phosphatase UNIT', 'ALP', 'Liver Function Test'),

    // BY..BZ — PSA
    val('PSA', 'PSA', 'PSA'),
    unit('PSA (ng/ml)', 'PSA', 'PSA'),

    // CA..CF — Thyroid
    val('TSH', 'TSH', 'Thyroid Function Test'),
    unit('TSH (µIU/mL)', 'TSH', 'Thyroid Function Test'),
    val('T3', 'T3', 'Thyroid Function Test'),
    unit('T3 UNIT', 'T3', 'Thyroid Function Test'),
    val('T4', 'T4', 'Thyroid Function Test'),
    unit('T4 UNIT', 'T4', 'Thyroid Function Test'),

    // CG..CI — Urinalysis (assembled from component tests)
    { header: 'Urine - Physical', group: 'Urine Analysis', fn: (m) => assemble(m, URINE_PHYSICAL) },
    { header: 'Urine - Chemical', group: 'Urine Analysis', fn: (m) => assemble(m, URINE_CHEMICAL) },
    { header: 'Urine - Microscopic', group: 'Urine Analysis', fn: (m) => assemble(m, URINE_MICRO) },
];

// Contiguous section groups for the grid's top header row (label + span).
export function sectionGroups(columns) {
    const groups = [];
    columns.forEach((c) => {
        const label = c.group || '';
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.span += 1;
        else groups.push({ label, span: 1 });
    });
    return groups;
}

/**
 * Map one normalized record (from /reports/voucher-rows) to an array of cell
 * strings aligned 1:1 with `columns`.
 */
export function buildVoucherRow(rec, ctx = {}, columns = VOUCHER_COLUMNS) {
    const map = resultMap(rec);
    return columns.map((col) => {
        if (col.blank) return '';
        if (col.field) return col.field(rec, ctx) ?? '';
        if (col.fn) return col.fn(map) ?? '';
        if (col.test) {
            const r = map.get(col.test.code);
            if (!r) return '';
            const cell = col.test.unit ? r.unit : r.value;
            return cell != null ? String(cell) : '';
        }
        return '';
    });
}

/**
 * One Ministry submission workbook as the page shows it: its columns, which
 * loaded orders belong on it, the text when none do, and an optional note
 * shown above its grid.
 */
export function voucherSheet({ key, label, columns, includes = () => true, emptyText, note = '' }) {
    return {
        key,
        label,
        columns,
        includes,
        emptyText,
        note,
        headers: columns.map((c) => c.header),
        groups: sectionGroups(columns),
    };
}

export const VOUCHER_SHEET = voucherSheet({
    key: 'general',
    label: 'General',
    columns: VOUCHER_COLUMNS,
    emptyText: 'No finalized orders in this period.',
});
